import { useState } from 'react';
import { Button, Dialog, DialogTitle } from 'components/Elements';
import { useNotificationStore } from 'stores/notifications';
import { X, Send, ChevronDown } from 'lucide-react';
import {
  CheckboxField,
  InputField,
  RegularTextAreaField,
} from 'components/Form';

export const SendMessage = () => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState({
    recipientType: 'all',
    userId: '',
    subject: '',
    message: '',
    channels: {
      push: true,
      email: true,
      sms: false,
      inApp: true,
    },
  });
  const [isSending, setSending] = useState(false);
  const { addNotification } = useNotificationStore();

  const handleOpen = () => setDialogOpen(true);
  const handleClose = () => {
    setDialogOpen(false);
    setSending(false);
    setFormState({
      recipientType: 'all',
      userId: '',
      subject: '',
      message: '',
      channels: { push: true, email: true, sms: false, inApp: true },
    });
  };

  const handleChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (channel) => {
    setFormState((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: !prev.channels[channel],
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formState.message.trim() || !formState.subject.trim()) return;

    setSending(true);

    try {
      // Simulated API delay
      await new Promise((res) => setTimeout(res, 1000));
      addNotification({ type: 'success', title: 'Message sent successfully' });
      handleClose();
    } catch (error) {
      addNotification({ type: 'error', title: 'Failed to send message' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button
        variant="glass"
        onClick={handleOpen}
        endIcon={<ChevronDown size={16} className="text-white" />}
      >
        Send Message
      </Button>

      <Dialog isOpen={isDialogOpen} onClose={handleClose}>
        <form
          onSubmit={handleSubmit}
          className="relative bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] shadow-2xl rounded-2xl px-6 pt-5 pb-6 text-white sm:my-8 sm:max-w-lg sm:w-full"
        >
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-xl font-bold">
              Send Message
            </DialogTitle>
            <button
              onClick={handleClose}
              type="button"
              className="text-white/60 hover:text-white/90 transition"
            >
              <X />
            </button>
          </div>

          {/* Recipient Selection */}
          <div className="mb-4 space-y-2">
            <label className="text-sm font-medium">Recipient</label>
            <select
              value={formState.recipientType}
              onChange={(e) => handleChange('recipientType', e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white/90 text-sm"
            >
              <option value="all">All Users</option>
              <option value="user">Specific User</option>
            </select>
            {formState.recipientType === 'user' && (
              <InputField
                placeholder="Enter user ID"
                value={formState.userId}
                onChange={(e) => handleChange('userId', e.target.value)}
              />
            )}
          </div>

          {/* Channels */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Delivery Channels
            </label>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <label className="flex items-center gap-2">
                <CheckboxField
                  checked={formState.channels.push}
                  onChange={() => handleCheckboxChange('push')}
                />
                Push Notification
              </label>
              <label className="flex items-center gap-2">
                <CheckboxField
                  checked={formState.channels.email}
                  onChange={() => handleCheckboxChange('email')}
                />
                Email
              </label>
              <label className="flex items-center gap-2">
                <CheckboxField
                  checked={formState.channels.sms}
                  onChange={() => handleCheckboxChange('sms')}
                />
                SMS
              </label>
              <label className="flex items-center gap-2">
                <CheckboxField
                  checked={formState.channels.inApp}
                  onChange={() => handleCheckboxChange('inApp')}
                />
                In-App Message
              </label>
            </div>
          </div>

          {/* Subject */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Subject</label>
            <InputField
              value={formState.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
              placeholder="Enter message subject"
              required
            />
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Message</label>
            <RegularTextAreaField
              rows={5}
              value={formState.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Write your message here..."
              required
            />
          </div>

          <Button
            type="submit"
            startIcon={<Send size={16} />}
            className="w-full"
            isLoading={isSending}
            disabled={!formState.message.trim() || !formState.subject.trim()}
          >
            {isSending ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </Dialog>
    </>
  );
};
